import React from 'react';
import Relay from 'react-relay';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import AppPanel from '../fw/AppPanel.react';
import TestResultsTable from './TestResultsTable';
import TestRunHistory from './TestRunHistory';
import types from '../../common/model-api/types';

const PAGE_SIZE = 50;

export class FlowDefTestResultsView extends React.Component {
    static propTypes = {
        user: PropTypes.object.isRequired,
        intl: PropTypes.object.isRequired,
        params: PropTypes.object.isRequired,
    };

    state = {
        expandedFlows: new Set(),
        genericExpanded: false,
        pageInfo: {
            hasNextPage: true,
            endCursor: null,
        },
        first: PAGE_SIZE,
        isLoading: false,
    };

    componentDidMount() {
        const { user } = this.props;
        const { testInvocations } = user;

        if (testInvocations && testInvocations.edges && testInvocations.edges.length > 0) {
            const hasGenericTests = testInvocations.edges.some(({ node }) => !node.flow_id);
            this.setState({ genericExpanded: hasGenericTests });
        }

        if (testInvocations && testInvocations.pageInfo) {
            this.updatePageInfo(testInvocations.pageInfo);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.user.testInvocations && nextProps.user.testInvocations.pageInfo) {
            this.updatePageInfo(nextProps.user.testInvocations.pageInfo);
        }
    }

    toggleFlow = (flowId) => {
        const { expandedFlows } = this.state;
        const newExpanded = new Set(expandedFlows);
        if (newExpanded.has(flowId)) {
            newExpanded.delete(flowId);
        } else {
            newExpanded.add(flowId);
        }
        this.setState({ expandedFlows: newExpanded });
    };

    toggleGeneric = () => {
        this.setState(prevState => ({
            genericExpanded: !prevState.genericExpanded,
        }));
    };

    refreshData = () => {
        this.props.relay.forceFetch();
    };

    paginationGeneration = 0;

    componentWillUnmount() {
        this.unmounted = true;
    }

    loadMoreTests = () => {
        if (!this.state.pageInfo.hasNextPage || this.state.isLoading) return;

        const first = this.state.first + PAGE_SIZE;
        const generation = ++this.paginationGeneration;
        this.setState({ isLoading: true, first });
        this.props.relay.setVariables({
            after: null,
            first,
        }, (readyState) => {
            // Release the guard only for the active request, and only once it reaches a final
            // state (done, error or aborted). Ready-but-not-done and superseded callbacks keep it.
            if (this.unmounted || generation !== this.paginationGeneration) return;
            if (!readyState || !(readyState.done || readyState.error || readyState.aborted)) return;
            this.setState({ isLoading: false });
        });
    };

    updatePageInfo = (pageInfo) => {
        this.setState({ pageInfo });
    };

    render() {
        const { flowDefId } = this.props.params;
        const { testInvocations } = this.props.user;
        const { expandedFlows, genericExpanded } = this.state;

        const testsByFlow = {};
        const genericTests = [];

        if (testInvocations && testInvocations.edges) {
            testInvocations.edges.forEach(({ node: test }) => {
                if (test.flow_id) {
                    const flowId = test.flow.id;
                    if (!testsByFlow[flowId]) {
                        testsByFlow[flowId] = {
                            id: flowId,
                            name: test.flow.flowTitle || test.flow.name || 'Unnamed Flow',
                            tests: [],
                        };
                    }
                    testsByFlow[flowId].tests.push(test);
                } else {
                    genericTests.push(test);
                }
            });
        }

        const breadcrumbParams = [
            { type: types['flowDefs'], data: { realId: flowDefId, id: flowDefId, name: 'Flow Definition' } },
        ];

        return (
            <AppPanel
                breadcrumbParams={breadcrumbParams}
                title={this.props.intl.formatMessage({ id: 'snapshotTesting.pageTitleFlowDef', defaultMessage: 'Flow Definition Test Results' })}
            >
                <div className="flex-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
                    <h3 style={{ margin: 0 }}>
                        <i className="fa fa-flask" /> <FormattedMessage defaultMessage="Test Results for Flow Definition" id="snapshotTesting.resultsTitleFlowDef" />
                    </h3>
                    <button className="btn btn-default" onClick={this.refreshData}>
                        <i className="fa fa-refresh" /> <FormattedMessage defaultMessage="Refresh" id="common.refresh" />
                    </button>
                </div>

                {genericTests.length > 0 && (
                    <div className="panel panel-default" style={{ marginBottom: '1.5em' }}>
                        <button
                            aria-controls="snapshot-testing-generic-flowdef-tests"
                            aria-expanded={genericExpanded}
                            className="panel-heading"
                            onClick={this.toggleGeneric}
                            style={{ cursor: 'pointer', width: '100%', border: 0, textAlign: 'left' }}
                            type="button"
                        >
                            <span className="panel-title" style={{ display: 'block' }}>
                                <i className={`fa fa-fw ${genericExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                                <FormattedMessage defaultMessage="Generic FlowDef Tests ({count})" id="snapshotTesting.genericTests" values={{ count: genericTests.length }} />
                            </span>
                        </button>
                        {genericExpanded && (
                            <div className="panel-body" id="snapshot-testing-generic-flowdef-tests">
                                <TestResultsTable invocations={genericTests} lazyRunHistory onRerun={this.refreshData} />
                            </div>
                        )}
                    </div>
                )}

                {Object.values(testsByFlow).map(({ id, name, tests }) => (
                    <div className="panel panel-default" key={id} style={{ marginBottom: '1.5em' }}>
                        <button
                            aria-controls={`snapshot-testing-flowdef-flow-${id}`}
                            aria-expanded={expandedFlows.has(id)}
                            className="panel-heading"
                            onClick={() => this.toggleFlow(id)}
                            style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', border: 0, textAlign: 'left' }}
                            type="button"
                        >
                            <span className="panel-title" style={{ display: 'block', margin: 0 }}>
                                <i className={`fa fa-fw ${expandedFlows.has(id) ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                                <span>{name}</span>
                                <span className="label label-info" style={{ marginLeft: '10px' }}>
                                    <FormattedMessage
                                        defaultMessage="{count, plural, one {# test} other {# tests}}"
                                        id="snapshotTesting.testsCount"
                                        values={{ count: tests.length }}
                                    />
                                </span>
                            </span>
                        </button>
                        {expandedFlows.has(id) && (
                            <div className="panel-body" id={`snapshot-testing-flowdef-flow-${id}`}>
                                <TestResultsTable invocations={tests} lazyRunHistory onRerun={this.refreshData} />
                            </div>
                        )}
                    </div>
                ))}

                {Object.keys(testsByFlow).length === 0 && genericTests.length === 0 && (
                    <div className="alert alert-info">
                        <FormattedMessage defaultMessage="No tests found for this flow definition." id="snapshotTesting.noTestsFound" />
                    </div>
                )}

                {this.state.pageInfo.hasNextPage && (
                    <div style={{ marginTop: '1.5em', textAlign: 'center' }}>
                        <button
                            className="btn btn-default"
                            disabled={this.state.isLoading}
                            onClick={this.loadMoreTests}
                        >
                            {this.state.isLoading ? (
                                <span><i className="fa fa-spinner fa-spin"></i> <FormattedMessage defaultMessage="Loading..." id="snapshotTesting.loadingMoreTests" /></span>
                            ) : (
                                <FormattedMessage defaultMessage="Load More Tests" id="snapshotTesting.loadMoreTests" />
                            )}
                        </button>
                    </div>
                )}
            </AppPanel>
        );
    }
}

export default Relay.createContainer(injectIntl(FlowDefTestResultsView), {
    initialVariables: {
        flowDefId: null,
        after: null,
        first: PAGE_SIZE,
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                testInvocations(flowDefId: $flowDefId, first: $first, after: $after) {
                    edges {
                        node {
                            id
                            realId
                            version
                            name
                            organisation_id
                            flow_id
                            flow {
                                 id
                                 flowTitle
                                 name
                             }
                            sourceFlowInvocation_id
                            lastStatus
                            lastRunAt
                            lastDuration
                            lastError
                            lastSuccessfulRun {
                                id
                                realId
                                status
                                duration
                                createdAt
                            }
                            lastUnsuccessfulRun {
                                id
                                realId
                                status
                                duration
                                createdAt
                                error
                            }
                            ${TestRunHistory.getFragment('testInvocation')}
                        }
                    }
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                }
            }
        `,
    },
});
